import { useCallback } from "react";
import {
  useAttachExternalContentMutation,
  ConnectorType,
  ExternalSourceChunkingStrategyInput,
  AttachExternalContentMutation,
} from "@axio/graphql/generated/graphql";
import Sentry from "../../utils/sentry";

export interface AttachExternalParams {
  connectorType: ConnectorType;
  externalItemIds: string[];
  strategy?: ExternalSourceChunkingStrategyInput;
}

export interface AttachExternalContentItemResult {
  externalItemId: string;
  jobId: string | null;
  success: boolean;
  message?: string | null;
}

export interface AttachExternalResult {
  attachExternal: (params: AttachExternalParams) => Promise<AttachExternalContentMutation | null>;
  loading: boolean;
  error: Error | null;
  result?: {
    success: boolean;
    message?: string | null;
    requestedCount: number;
    attachedCount: number;
    failedCount: number;
    failedExternalItemIds: string[];
    queuedJobIds: string[];
    itemResults: AttachExternalContentItemResult[];
  };
}

/**
 * Hook to attach external content items via the attachExternalContent mutation.
 *
 * Wraps the generated `useAttachExternalContentMutation` hook with a simplified API:
 * - Provides default chunking strategy (RecursiveTextSplitting)
 * - Handles targetContext construction
 * - Returns flattened result structure
 *
 * @example
 * ```tsx
 * const { attachExternal, loading, error, result } = useAttachExternalContent();
 *
 * // Attach items with default strategy
 * const data = await attachExternal({
 *   connectorType: ConnectorType.GoogleDrive,
 *   externalItemIds: ['file-id-1', 'file-id-2']
 * });
 *
 * // Attach with custom strategy
 * const data = await attachExternal({
 *   connectorType: ConnectorType.OneDrive,
 *   externalItemIds: ['doc-id-1'],
 *   strategy: ExternalSourceChunkingStrategyInput.UnstructuredWithImages
 * });
 *
 * // Check result
 * if (result) {
 *   console.log(`Attached ${result.attachedCount} of ${result.requestedCount}`);
 *   console.log(`Job IDs:`, result.queuedJobIds);
 * }
 * ```
 */
export function useAttachExternalContent(): AttachExternalResult {
  const [mutate, { loading, error, data }] = useAttachExternalContentMutation();

  const attachExternal = useCallback(
    async (params: AttachExternalParams) => {
      const {
        connectorType,
        externalItemIds,
        strategy = ExternalSourceChunkingStrategyInput.RecursiveTextSplitting,
      } = params;

      try {
        const response = await mutate({
          variables: {
            connectorType,
            externalItemIds,
            targetContext: {
              strategy,
            },
          },
        });

        return response.data ?? null;
      } catch (err) {
        Sentry.captureException(err, {
          tags: {
            feature: "attachExternalContent",
            connectorType,
          },
          extra: {
            params,
          },
        });
        throw err;
      }
    },
    [mutate]
  );

  const result = data?.attachExternalContent
    ? {
        success: data.attachExternalContent.success,
        message: data.attachExternalContent.message,
        requestedCount: data.attachExternalContent.data?.requestedCount ?? 0,
        attachedCount: data.attachExternalContent.data?.attachedCount ?? 0,
        failedCount: data.attachExternalContent.data?.failedCount ?? 0,
        failedExternalItemIds: data.attachExternalContent.data?.failedExternalItemIds ?? [],
        queuedJobIds: data.attachExternalContent.data?.queuedJobIds ?? [],
        itemResults: data.attachExternalContent.data?.itemResults ?? [],
      }
    : undefined;

  return {
    attachExternal,
    loading,
    error: error ?? null,
    result,
  };
}
